/* eslint-disable react-refresh/only-export-components */
import { Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { lazyWithRetryNamed } from '@/lib/lazy-with-retry';

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

import { AcceptInvitation } from '@/pages/AcceptInvitation';
import { AuthCallback } from '@/pages/AuthCallback';
import { ForgotPassword } from '@/pages/ForgotPassword';
// Auth pages - loaded eagerly as they are entry points
import { Login } from '@/pages/Login';
import { Register } from '@/pages/Register';
import { ResetPassword } from '@/pages/ResetPassword';

// Core pages - lazy loaded with retry for chunk staleness after deployments
const Dashboard = lazyWithRetryNamed(() => import('@/pages/Dashboard'), 'Dashboard');
const Projects = lazyWithRetryNamed(() => import('@/pages/Projects'), 'Projects');
const ProjectDetailPage = lazyWithRetryNamed(
  () => import('@/pages/ProjectDetailPage'),
  'ProjectDetailPage',
);
const GoLiveChecklistCreatePage = lazyWithRetryNamed(
  () => import('@/pages/GoLiveChecklistCreatePage'),
  'GoLiveChecklistCreatePage',
);
const RunbookDetailPage = lazyWithRetryNamed(
  () => import('@/pages/RunbookDetailPage'),
  'RunbookDetailPage',
);
const MyProjects = lazyWithRetryNamed(() => import('@/pages/MyProjects'), 'MyProjects');
const Issues = lazyWithRetryNamed(() => import('@/pages/Issues'), 'Issues');
const IssueDetailPage = lazyWithRetryNamed(
  () => import('@/pages/IssueDetailPage'),
  'IssueDetailPage',
);
const Tasks = lazyWithRetryNamed(() => import('@/pages/Tasks'), 'Tasks');
const Documents = lazyWithRetryNamed(() => import('@/pages/Documents'), 'Documents');
const Settings = lazyWithRetryNamed(() => import('@/pages/Settings'), 'Settings');

// Test management pages - lazy loaded (heavy components)
const TestPlans = lazyWithRetryNamed(() => import('@/pages/TestPlans'), 'TestPlans');
const TestPlanCreatePage = lazyWithRetryNamed(
  () => import('@/pages/TestPlanCreatePage'),
  'TestPlanCreatePage',
);
const TestPlanDetailPage = lazyWithRetryNamed(
  () => import('@/pages/TestPlanDetailPage'),
  'TestPlanDetailPage',
);
const TestExecutionPage = lazyWithRetryNamed(
  () => import('@/pages/TestExecutionPage'),
  'TestExecutionPage',
);

// Admin pages - lazy loaded (rarely accessed)
const AdminPanel = lazyWithRetryNamed(() => import('@/pages/AdminPanel'), 'AdminPanel');
const TenantDetailPage = lazyWithRetryNamed(() => import('@/pages/admin'), 'TenantDetailPage');
const UserDetailPage = lazyWithRetryNamed(() => import('@/pages/admin'), 'UserDetailPage');

// Help pages - lazy loaded
const Help = lazyWithRetryNamed(() => import('@/pages/Help'), 'Help');

// UI Demo page - showcase premium UI features
const UIDemoPage = lazyWithRetryNamed(() => import('@/pages/UIDemoPage'), 'UIDemoPage');

import { PlatformLayout } from '@/components/platform/PlatformLayout';
import { PlatformProtectedRoute } from '@/components/platform/ProtectedRoute';
// Platform (Control Plane) pages - separate auth flow
import { PlatformLogin } from '@/pages/platform/Login';

const PlatformDashboard = lazyWithRetryNamed(
  () => import('@/pages/platform/Dashboard'),
  'PlatformDashboard',
);
const PlatformOrganizations = lazyWithRetryNamed(
  () => import('@/pages/platform/Organizations'),
  'PlatformOrganizations',
);
const PlatformOrganizationDetail = lazyWithRetryNamed(
  () => import('@/pages/platform/OrganizationDetail'),
  'OrganizationDetail',
);
const PlatformProvisioning = lazyWithRetryNamed(
  () => import('@/pages/platform/Provisioning'),
  'PlatformProvisioning',
);
const PlatformConfig = lazyWithRetryNamed(
  () => import('@/pages/platform/Config'),
  'PlatformConfig',
);
const PlatformAuditLogs = lazyWithRetryNamed(
  () => import('@/pages/platform/AuditLogs'),
  'PlatformAuditLogs',
);
const PlatformSettings = lazyWithRetryNamed(
  () => import('@/pages/platform/Settings'),
  'PlatformSettings',
);
const PlatformPricing = lazyWithRetryNamed(
  () => import('@/pages/platform/Pricing'),
  'PlatformPricing',
);
const PlatformPromotions = lazyWithRetryNamed(
  () => import('@/pages/platform/Promotions'),
  'PlatformPromotions',
);

export const router = createBrowserRouter([
  // Public routes
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/register',
    element: <Register />,
  },
  {
    path: '/auth/callback',
    element: <AuthCallback />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  {
    path: '/invite/accept',
    element: <AcceptInvitation />,
  },
  // Platform (Control Plane) routes - separate auth flow
  {
    path: '/platform/login',
    element: <PlatformLogin />,
  },
  {
    path: '/platform',
    element: (
      <PlatformProtectedRoute>
        <PlatformLayout />
      </PlatformProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/platform/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PlatformDashboard />
          </Suspense>
        ),
      },
      {
        path: 'organizations',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PlatformOrganizations />
          </Suspense>
        ),
      },
      {
        path: 'organizations/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PlatformOrganizationDetail />
          </Suspense>
        ),
      },
      {
        path: 'provisioning',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PlatformProvisioning />
          </Suspense>
        ),
      },
      {
        path: 'config',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PlatformConfig />
          </Suspense>
        ),
      },
      {
        path: 'audit-logs',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PlatformAuditLogs />
          </Suspense>
        ),
      },
      {
        path: 'pricing',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PlatformPricing />
          </Suspense>
        ),
      },
      {
        path: 'promotions',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PlatformPromotions />
          </Suspense>
        ),
      },
      {
        path: 'settings',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PlatformSettings />
          </Suspense>
        ),
      },
    ],
  },
  // Protected routes
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Dashboard />
          </Suspense>
        ),
      },
      {
        path: 'projects',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Projects />
          </Suspense>
        ),
      },
      {
        path: 'projects/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProjectDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'projects/:id/go-live/create',
        element: (
          <Suspense fallback={<PageLoader />}>
            <GoLiveChecklistCreatePage />
          </Suspense>
        ),
      },
      {
        path: 'projects/:id/go-live/runbook/:runbookId',
        element: (
          <Suspense fallback={<PageLoader />}>
            <RunbookDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'my-projects',
        element: (
          <Suspense fallback={<PageLoader />}>
            <MyProjects />
          </Suspense>
        ),
      },
      {
        path: 'issues',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Issues />
          </Suspense>
        ),
      },
      // Issue detail with optional tab parameter
      {
        path: 'issues/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <IssueDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'issues/:id/:tab',
        element: (
          <Suspense fallback={<PageLoader />}>
            <IssueDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'tasks',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Tasks />
          </Suspense>
        ),
      },
      {
        path: 'test-plans',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TestPlans />
          </Suspense>
        ),
      },
      {
        path: 'test-plans/new',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TestPlanCreatePage />
          </Suspense>
        ),
      },
      {
        path: 'test-plans/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TestPlanDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'test-runs/:id/execute',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TestExecutionPage />
          </Suspense>
        ),
      },
      {
        path: 'documents',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Documents />
          </Suspense>
        ),
      },
      {
        path: 'help',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Help />
          </Suspense>
        ),
      },
      // UI Demo - showcase premium UI features
      {
        path: 'ui-demo',
        element: (
          <Suspense fallback={<PageLoader />}>
            <UIDemoPage />
          </Suspense>
        ),
      },
      // Settings with optional tab parameter
      {
        path: 'settings',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Settings />
          </Suspense>
        ),
      },
      {
        path: 'settings/:tab',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Settings />
          </Suspense>
        ),
      },
      // Admin Panel - unified admin interface
      {
        path: 'admin/panel',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AdminPanel />
          </Suspense>
        ),
      },
      // Admin Panel nested routes for user and tenant detail pages
      {
        path: 'admin/panel/users/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <UserDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'admin/panel/tenants/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TenantDetailPage />
          </Suspense>
        ),
      },
    ],
  },
]);
